"use client";

import { AreaSeries, LastPointMarker, LightweightChart, PriceLineOverlay } from "@/components/charts/lightweight-chart";
import { CryptoGuess } from "@/event-core/types";
import { useCryptoConnect } from "@/store/cryptoConnect";
import { get, moneyFormatCurrency } from "@/utils/utils";
import { ISeriesApi, UTCTimestamp } from "lightweight-charts";
import React, { useEffect, useRef, useState } from "react";
import { CryptoPricePoint } from "./api";

/**
 * 加密货币图表组件
 * 展示实时价格数据
 */
const cryptoAutoscaleInfoProvider = ( original: () => any ) => {
    const res = original();
    if ( res?.priceRange ) {
        const { minValue, maxValue } = res.priceRange;
        const diff = maxValue - minValue;
        // 设定极其敏感的最小波动范围 (万分之一)，但确保最小差值至少为 20，让视觉波动更明显且可控
        const minDiff = Math.max( 20, ( ( minValue + maxValue ) / 2 ) * 0.0001 );
        if ( diff < minDiff ) {
            const center = ( minValue + maxValue ) / 2;
            const half = minDiff / 2;
            return { priceRange: { minValue: center - half, maxValue: center + half } };
        }
    }
    return res;
};

type CryptoChartDataPoint = { time: UTCTimestamp; value: number };

function toChartDataPoint( point: CryptoPricePoint ): CryptoChartDataPoint {
    return { time: point.time as UTCTimestamp, value: point.value };
}

function sortAndDedupeChartData( points: CryptoChartDataPoint[] ) {
    return points.sort( ( a, b ) => a.time - b.time ).filter( ( point, index, sortedPoints ) =>
        index === sortedPoints.length - 1 || point.time !== sortedPoints[index + 1].time
    );
}

function CryptoChart(
    { symbol = "", expo = 0, guess, historyData = [] }: {
        symbol?: string;
        expo?: number;
        guess: CryptoGuess;
        historyData?: CryptoPricePoint[];
    },
) {
    const [ series, setSeries ] = useState<ISeriesApi<"Area"> | null>( null );
    const initConnection = useCryptoConnect( state => state.initConnection );
    const subscribe = useCryptoConnect( state => state.subscribe );
    const unsubscribe = useCryptoConnect( state => state.unsubscribe );
    const currentSymbol = useCryptoConnect( state => state.currentSymbol );
    const latestPricePoint = useCryptoConnect( state => state.latestPricePoint );

    const [ chartData, setChartData ] = useState<CryptoChartDataPoint[]>( [] );
    const [ appendData, setAppendData ] = useState<CryptoChartDataPoint | undefined>( undefined );
    const chartDataRef = useRef<CryptoChartDataPoint[]>( [] );

    // 初始化连接
    useEffect( () => {
        initConnection();
    }, [ initConnection ] );

    // 订阅价格数据
    useEffect( () => {
        if ( !symbol ) return;

        setChartData( [] );
        setAppendData( undefined );
        chartDataRef.current = [];
        subscribe( symbol, expo );

        return () => {
            unsubscribe();
        };
    }, [ expo, subscribe, symbol, unsubscribe ] );

    useEffect( () => {
        if ( !symbol ) return;

        const nextChartData = sortAndDedupeChartData( [
            ...chartDataRef.current,
            ...historyData.map( toChartDataPoint ),
        ] );

        chartDataRef.current = nextChartData;
        setChartData( nextChartData );
    }, [ historyData, symbol ] );

    // WS 推送只追加比当前末端更新的点，历史接口/缓存更新时会再统一去重合并。
    useEffect( () => {
        if ( !latestPricePoint ) return;
        if ( String( currentSymbol ) !== String( symbol ) ) return;

        const point = { time: latestPricePoint.time as UTCTimestamp, value: latestPricePoint.value };
        const currentChartData = chartDataRef.current;
        const lastPoint = currentChartData.length > 0 ? currentChartData[currentChartData.length - 1] : undefined;
        if ( lastPoint && point.time <= lastPoint.time ) return;

        const nextChartData = [ ...currentChartData, point ];
        chartDataRef.current = nextChartData;
        setAppendData( point );
    }, [ currentSymbol, latestPricePoint, symbol ] );

    // 平均价格使用竞猜的开盘价
    const avgPrice = get( guess, "openPrice", 0 );
    const latestPoint = appendData || chartData[chartData.length - 1];

    return (
        <LightweightChart
            priceFormatter={( price: number ) =>
                moneyFormatCurrency( price, { currency: "USD", precision: 0, useScale: false, useExchange: false } )}
            options={{
                rightPriceScale: {
                    visible: true,
                    alignLabels: false,
                    borderVisible: false,
                    scaleMargins: { top: 0.08, bottom: 0.08 }, // 缩小留白比例，让图表在小范围内波动更明显
                },
                // 禁止缩放
                handleScale: { axisPressedMouseMove: false, mouseWheel: false, pinch: false },
                // 允许左右拖拽滑动
                handleScroll: { pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false, mouseWheel: false },
            }}
        >
            <AreaSeries
                ref={setSeries}
                data={chartData}
                appendData={appendData}
                lastPrice={latestPoint?.value}
                options={{
                    lineColor: "#FF3D4C",
                    lineWidth: 2,
                    lastValueVisible: false,
                    priceLineVisible: true,
                    crosshairMarkerVisible: true,
                    autoscaleInfoProvider: cryptoAutoscaleInfoProvider, // 应用灵敏的自动缩放器
                }}
            />

            <PriceLineOverlay series={series} avgPrice={avgPrice} lastPrice={latestPoint?.value} label="Last close" />

            {/* 追加末端脉冲原点 */}
            {latestPoint && <LastPointMarker series={series} data={latestPoint} color="#FF3D4C" />}
        </LightweightChart>
    );
}

export default CryptoChart;
